import React, { useState, useEffect } from 'react';
import { X, Plus, Search, UserPlus, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { createNeed, getUsers } from '../api';
import { User } from '../types';

interface Props {
  currentUser: User;
  onClose: () => void;
  onCreated: (id: string) => void;
}

export default function CreateNeedModal({ currentUser, onClose, onCreated }: Props) {
  const [department, setDepartment] = useState('');
  const [businessProblem, setBusinessProblem] = useState('');
  const [headcount, setHeadcount] = useState(1);
  const [urgency, setUrgency] = useState('normal');
  const [needSource, setNeedSource] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getUsers().then(setAllUsers).catch(() => {});
  }, []);

  const availableUsers = allUsers.filter(u =>
    u.id !== currentUser.id &&
    u.status === 'active' &&
    (userSearch
      ? (u.display_name || u.username).toLowerCase().includes(userSearch.toLowerCase())
      : true)
  );

  const selectedUsers = allUsers.filter(u => memberIds.includes(u.id));

  const toggleMember = (uid: string) => {
    setMemberIds(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
  };

  const handleSubmit = async () => {
    if (!businessProblem.trim()) {
      toast.error('请填写业务问题');
      return;
    }
    setSubmitting(true);
    try {
      const result = await createNeed({
        department: department.trim(),
        business_problem: businessProblem.trim(),
        headcount,
        urgency,
        member_ids: memberIds,
        need_source: needSource.trim(),
      });
      toast.success('需求已创建，AI正在辩论中...');
      onCreated(result.id);
    } catch (err: any) {
      toast.error(err.response?.data?.message || '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-modal-in max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">新建岗位需求</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 text-gray-400 transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Department */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">部门</label>
            <input value={department} onChange={e => setDepartment(e.target.value)}
              placeholder="如：技术部、产品部"
              className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300" />
          </div>

          {/* Business Problem */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">
              业务问题 / 需求背景 <span className="text-gray-500">*</span>
            </label>
            <textarea value={businessProblem} onChange={e => setBusinessProblem(e.target.value)}
              placeholder="描述你要解决什么问题、为什么需要招人..."
              rows={4}
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 resize-none" />
          </div>

          {/* Need source */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">需求来源</label>
            <select value={needSource} onChange={e => setNeedSource(e.target.value)}
              className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 bg-white">
              <option value="">未指定</option>
              <option value="业务增长">业务增长</option>
              <option value="人员替换">人员替换</option>
              <option value="新业务拓展">新业务拓展</option>
              <option value="内部调动">内部调动</option>
              <option value="为实习生">为实习生</option>
              <option value="其他">其他</option>
            </select>
          </div>

          {/* Headcount + Urgency */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">需求人数</label>
              <input type="number" min={1} max={99} value={headcount} onChange={e => setHeadcount(parseInt(e.target.value) || 1)}
                className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300" />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600 mb-1 block">紧急程度</label>
              <select value={urgency} onChange={e => setUrgency(e.target.value)}
                className="w-full h-9 px-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 bg-white">
                <option value="low">不急</option>
                <option value="normal">正常</option>
                <option value="high">紧急</option>
                <option value="urgent">非常紧急</option>
              </select>
            </div>
          </div>

          {/* Collaborators */}
          <div>
            <label className="text-xs font-medium text-gray-600 mb-1 block">
              <UserPlus size={12} className="inline mr-1" />协作成员
            </label>
            <p className="text-[11px] text-gray-400 mb-2">选择可查看和参与讨论的成员（创建人自动加入）</p>

            {/* Selected chips */}
            {selectedUsers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {selectedUsers.map(u => (
                  <span key={u.id}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-primary-50 text-primary-600">
                    {u.display_name || u.username}
                    <button onClick={() => toggleMember(u.id)} className="hover:text-gray-500">
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* User search */}
            <div className="relative mb-1.5">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={userSearch} onChange={e => setUserSearch(e.target.value)}
                placeholder="搜索成员..."
                className="w-full h-8 pl-8 pr-3 rounded-lg border border-gray-200 text-xs focus:outline-none focus:ring-1 focus:ring-primary-400 bg-gray-50" />
            </div>

            {/* User list */}
            <div className="max-h-[140px] overflow-y-auto border border-gray-100 rounded-lg">
              {availableUsers.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-3">没有可选成员</p>
              ) : (
                availableUsers.slice(0, 20).map(u => {
                  const selected = memberIds.includes(u.id);
                  return (
                    <button key={u.id} onClick={() => toggleMember(u.id)}
                      className={clsx(
                        'w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-gray-50 transition-colors',
                        selected && 'bg-primary-50/50'
                      )}
                    >
                      <div className={clsx(
                        'w-5 h-5 rounded border flex items-center justify-center transition-colors',
                        selected ? 'bg-primary-600 border-primary-600' : 'border-gray-300'
                      )}>
                        {selected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-medium text-gray-700">{u.display_name || u.username}</span>
                        <span className="ml-2 text-[10px] text-gray-400">
                          {u.role === 'admin' ? '管理员' : u.role === 'hr' ? 'HR' : '面试官'}
                        </span>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 px-5 py-3 border-t border-gray-100 flex items-center justify-between">
          <p className="text-[11px] text-gray-400">提交后AI将自动进行三方辩论分析</p>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              取消
            </button>
            <button onClick={handleSubmit} disabled={submitting || !businessProblem.trim()}
              className="px-5 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-1.5">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
              {submitting ? '创建中...' : '创建需求'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

